import { GraphQLInputV1, GraphQLOutputV1, sqGraphQLApi } from '@/sdk';
import { errorToast } from '@/utilities/toast.utilities';
import { MAX_TABLE_ROWS } from '@/main/app.constants';
import {
  MaterializedTableFilterInput,
  MaterializedTableFormulaInput,
  MaterializedTablePropertyColumnInput,
} from '@/tableDefinitionEditor/materializedTable.types';

export interface FetchMaterializedTableInput {
  tableDefinitionId: string;
  columnsToInclude?: string[];
  propertiesToInclude?: MaterializedTablePropertyColumnInput[];
  includeOverrides?: boolean;
  tableFormulas?: MaterializedTableFormulaInput[];
  cancellationGroup?: string;
  limit?: number;
  cursorKey?: string;
  filter?: MaterializedTableFilterInput;
}
export const fetchMaterializedTable = async ({
  tableDefinitionId,
  columnsToInclude,
  propertiesToInclude,
  includeOverrides,
  tableFormulas,
  cancellationGroup,
  limit,
  cursorKey,
  filter,
}: FetchMaterializedTableInput): Promise<GraphQLOutputV1> => {
  const GET_MATERIALIZED_TABLE_QUERY =
    'query GetTable($id: String!, $filter: FilterInput, $limit: Int!, $columnsToInclude: [String!], ' +
    '$propertiesToInclude: [PropertiesForItemUUIDColumnInput!], $includeOverrides: Boolean!, ' +
    '$tableFormulas: [TableFormulaInput!], $sorts: [SortInput!], $cursor: CursorInput) { table(id: $id, filter:' +
    ' $filter, limit: $limit, columnsToInclude: $columnsToInclude, propertiesToInclude: $propertiesToInclude, ' +
    'includeOverrides: $includeOverrides, tableFormulas: $tableFormulas, sorts: $sorts, cursor: $cursor)' +
    '{ rows headers { name type } hasMore cursorKey } }';

  const inputObject: GraphQLInputV1 = {
    query: GET_MATERIALIZED_TABLE_QUERY,
    variables: {
      id: tableDefinitionId,
      limit: limit || MAX_TABLE_ROWS,
      columnsToInclude,
      propertiesToInclude,
      includeOverrides,
      tableFormulas,
      sorts: [],
      ...(cursorKey && {
        cursor: {
          cursorKey,
        },
      }),
      filter,
    },
  };
  try {
    const response = await sqGraphQLApi.graphql(inputObject, {
      cancellationGroup,
    });
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return {};
  }
};

export interface ConditionTableStat {
  statistic: string;
  columnName?: string;
  itemId?: string;
}

interface ConditionTableInput {
  start: string | number;
  end: string | number;
  conditionAndMetricColumn: string;
  conditionProperties?: string[];
  capsuleProperties?: string[];
  additionalColumnProperties?: MaterializedTablePropertyColumnInput[];
  conditionStats?: ConditionTableStat[];
}

interface FetchConditionTableInput {
  materializedTableId: string;
  conditionTableInput: ConditionTableInput;
}

export async function fetchConditionTableOverMaterializedTable({
  materializedTableId,
  conditionTableInput,
}: FetchConditionTableInput): Promise<GraphQLOutputV1> {
  const QUERY = `
  query ConditionTable(
  $id: String!
  $limit: Int!
  $conditionTableInput: ConditionTableInput!
) {
  conditionTable(
    id: $id
    limit: $limit
    conditionTableInput: $conditionTableInput
  ) {
    rows
    headers {
      name
      type
    }
  }
}`;

  const inputObject: GraphQLInputV1 = {
    query: QUERY,
    variables: {
      id: materializedTableId,
      limit: MAX_TABLE_ROWS,
      conditionTableInput,
    },
  };
  try {
    const response = await sqGraphQLApi.graphql(inputObject);
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return {};
  }
}
